import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useState } from "react";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { PANListResponse } from "src/models/PAN.model";
import { useGeneratePanAutoDownloadMutation } from "src/services/PANService";
import { showToast } from "src/utils/toaster/showToast";

type IncludeKey = "panForm" | "affidavit" | "photo" | "sign" | "vendorXlsx";
type Include = Record<IncludeKey, boolean>;

type Props = {
  onClose: () => void;
  applications: PANListResponse[];
  /** Called after a zip was downloaded. */
  onDone: () => void;
};

const OPTIONS: { key: IncludeKey; label: string; hint?: string }[] = [
  { key: "panForm", label: "PAN Form PDF" },
  { key: "affidavit", label: "Affidavit Certificate", hint: "only where one exists" },
  { key: "photo", label: "Photo" },
  { key: "sign", label: "Signature" },
  { key: "vendorXlsx", label: "PAN Vendor Data Report (XLSX)" },
];

const ALL_ON: Include = {
  panForm: true,
  affidavit: true,
  photo: true,
  sign: true,
  vendorXlsx: true,
};

/** The last choice is remembered per browser; it is only a convenience. */
const STORAGE_KEY = "panGenerateAutoDownloadInclude";

const MARK_DONE_KEY = "panGenerateAutoDownloadMarkDone";

const readMarkDone = (): boolean => {
  try {
    return localStorage.getItem(MARK_DONE_KEY) !== "false";
  } catch {
    return true;
  }
};

const readSaved = (): Include => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && typeof saved === "object") {
      return OPTIONS.reduce(
        (acc, { key }) => ({ ...acc, [key]: saved[key] !== false }),
        { ...ALL_ON }
      );
    }
  } catch {
    // ignore
  }
  return { ...ALL_ON };
};

/** Error bodies come back as JSON (or a Blob of JSON) — dig the message out. */
const errorMessage = async (error: any): Promise<string> => {
  try {
    let data = error?.data;
    if (data instanceof Blob) data = JSON.parse(await data.text());
    return data?.message || "Download failed";
  } catch {
    return "Download failed";
  }
};

/**
 * PAN GENERATE "AUTO DOWNLOAD": pick which files, get one zip for all the
 * ticked applications (folders PDF / AFFIDAVIT / PHOTO / SIG, the vendor sheet
 * at the top, MISSING.txt listing anything not found), then optionally move
 * the fully downloaded ones to DONE.
 */
const AutoDownloadDialogWrapper = ({ onClose, applications, onDone }: Props) => {
  const [include, setInclude] = useState<Include>(readSaved);
  const [markDone, setMarkDone] = useState<boolean>(readMarkDone);
  const [download, { isLoading }] = useGeneratePanAutoDownloadMutation();

  const count = applications?.length || 0;
  const plural = count > 1 ? "s" : "";
  const anySelected = Object.values(include).some(Boolean);

  const handleDownload = async () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(include));
      localStorage.setItem(MARK_DONE_KEY, String(markDone));
    } catch {
      // ignore
    }

    const res: any = await download({
      ids: applications.map((application) => application._id),
      include,
      markDone,
    });

    if (res?.error) {
      showToast("error", await errorMessage(res.error));
      return;
    }

    const { blob, fileName, applicationCount, missingCount, doneCount } = res.data || {};
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    const skipped = count - (applicationCount || 0);
    showToast(
      "success",
      `Downloaded ${applicationCount} application${applicationCount > 1 ? "s" : ""}.` +
        (skipped > 0 ? ` ${skipped} skipped (not in GENERATE / not yours).` : "") +
        (markDone ? ` ${doneCount} moved to DONE.` : "") +
        (missingCount
          ? ` ${missingCount} file(s) missing — see MISSING.txt${
              markDone ? " (those stay in GENERATE)" : ""
            }.`
          : "")
    );
    onDone();
    onClose();
  };

  return (
    <Dialog open={true} maxWidth="xs" fullWidth>
      <div className="px-4">
        <div className="text-md py-2 font-medium text-primary-main">
          Auto Download — {count} application{plural}
        </div>
      </div>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {OPTIONS.map(({ key, label, hint }) => (
              <label key={key} className="flex items-center gap-2 text-[14px] cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-primary-main"
                  checked={include[key]}
                  disabled={isLoading}
                  onChange={(e) =>
                    setInclude((prev) => ({ ...prev, [key]: e.target.checked }))
                  }
                />
                <span>{label}</span>
                {hint ? <span className="text-[12px] text-slate-400">({hint})</span> : null}
              </label>
            ))}
          </div>

          <label className="flex items-center gap-2 text-[14px] cursor-pointer border-t pt-3">
            <input
              type="checkbox"
              className="w-4 h-4 accent-primary-main"
              checked={markDone}
              disabled={isLoading}
              onChange={(e) => setMarkDone(e.target.checked)}
            />
            <span className="font-medium">Move to DONE after download</span>
          </label>

          <div className="text-[13px] text-slate-500">
            One zip with folders PDF, AFFIDAVIT, PHOTO and SIG (files named by
            acknowledgement number).
            {markDone
              ? " Applications whose files all downloaded move to DONE and get a row in the Application Status Flow."
              : ""}
          </div>

          <div className="flex justify-end gap-3">
            <ATMLoadingButton
              className="border-primary-main text-primary-main bg-white"
              disabled={isLoading}
              onClick={onClose}
            >
              Cancel
            </ATMLoadingButton>

            <ATMLoadingButton
              type="button"
              loadingText="Preparing zip..."
              isLoading={isLoading}
              disabled={!anySelected || isLoading}
              className="bg-primary-main"
              onClick={handleDownload}
            >
              Download
            </ATMLoadingButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AutoDownloadDialogWrapper;
